import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Edit2, Trash2, GripVertical, Save, X, RefreshCw } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import IconPicker, { CategoryIcon } from '../../components/ui/IconPicker'

interface Category {
    id: number
    slug: string
    name_en: string
    name_vi: string
    icon: string
    description_en?: string
    description_vi?: string
    sort_order: number
    is_active: number
}

export default function CategoriesManagement() {
    useTranslation()
    const { language } = useLanguageStore()

    const [categories, setCategories] = useState<Category[]>([])
    const [loading, setLoading] = useState(true)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [showAddForm, setShowAddForm] = useState(false)
    const [formData, setFormData] = useState({
        slug: '',
        name_en: '',
        name_vi: '',
        icon: 'Folder',
        description_en: '',
        description_vi: '',
        sort_order: 0
    })

    const fetchCategories = async () => {
        try {
            const res = await fetch('/api/admin/categories')
            const data = await res.json()
            if (data.success) {
                setCategories(data.categories || [])
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchCategories()
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        try {
            const url = editingId
                ? `/api/admin/categories/${editingId}`
                : '/api/admin/categories'

            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            })

            const data = await res.json()
            if (data.success) {
                fetchCategories()
                resetForm()
            } else {
                alert(data.error || 'Failed to save')
            }
        } catch (error) {
            console.error('Error saving category:', error)
        }
    }

    const handleEdit = (cat: Category) => {
        setEditingId(cat.id)
        setFormData({
            slug: cat.slug,
            name_en: cat.name_en,
            name_vi: cat.name_vi,
            icon: cat.icon,
            description_en: cat.description_en || '',
            description_vi: cat.description_vi || '',
            sort_order: cat.sort_order
        })
        setShowAddForm(true)
    }

    const handleDelete = async (id: number) => {
        if (!confirm(language === 'en' ? 'Delete this category?' : 'Xóa danh mục này?')) {
            return
        }

        try {
            const res = await fetch(`/api/admin/categories/${id}`, { method: 'DELETE' })
            const data = await res.json()
            if (data.success) {
                fetchCategories()
            }
        } catch (error) {
            console.error('Error deleting:', error)
        }
    }

    const resetForm = () => {
        setEditingId(null)
        setShowAddForm(false)
        setFormData({
            slug: '',
            name_en: '',
            name_vi: '',
            icon: 'Folder',
            description_en: '',
            description_vi: '',
            sort_order: 0
        })
    }

    const generateSlug = (text: string) => {
        return text.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '')
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {language === 'en' ? 'Category Management' : 'Quản Lý Danh Mục'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {language === 'en' ? 'Manage blog post categories' : 'Quản lý danh mục bài viết'}
                    </p>
                </div>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                    <Plus className="w-5 h-5" />
                    {language === 'en' ? 'Add Category' : 'Thêm danh mục'}
                </button>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
                <div className="bg-white border rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">
                            {editingId
                                ? (language === 'en' ? 'Edit Category' : 'Sửa danh mục')
                                : (language === 'en' ? 'New Category' : 'Danh mục mới')
                            }
                        </h2>
                        <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                {language === 'en' ? 'Name (English)' : 'Tên (Tiếng Anh)'}
                            </label>
                            <input
                                type="text"
                                value={formData.name_en}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        name_en: e.target.value,
                                        slug: editingId ? formData.slug : generateSlug(e.target.value)
                                    })
                                }}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                {language === 'en' ? 'Name (Vietnamese)' : 'Tên (Tiếng Việt)'}
                            </label>
                            <input
                                type="text"
                                value={formData.name_vi}
                                onChange={(e) => setFormData({ ...formData, name_vi: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Slug</label>
                            <input
                                type="text"
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                                required
                            />
                        </div>
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <IconPicker
                                    value={formData.icon}
                                    onChange={(icon) => setFormData({ ...formData, icon })}
                                    label="Icon"
                                />
                            </div>
                            <div className="w-24">
                                <label className="block text-sm font-medium mb-1">
                                    {language === 'en' ? 'Order' : 'Thứ tự'}
                                </label>
                                <input
                                    type="number"
                                    value={formData.sort_order}
                                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                        <div className="md:col-span-2 flex gap-3">
                            <button
                                type="submit"
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                                <Save className="w-4 h-4" />
                                {language === 'en' ? 'Save' : 'Lưu'}
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                {language === 'en' ? 'Cancel' : 'Hủy'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Categories Table */}
            <div className="bg-white border rounded-xl overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 w-12"></th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Icon</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">
                                {language === 'en' ? 'Name' : 'Tên'}
                            </th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Slug</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">
                                {language === 'en' ? 'Order' : 'Thứ tự'}
                            </th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">
                                {language === 'en' ? 'Status' : 'Trạng thái'}
                            </th>
                            <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">
                                {language === 'en' ? 'Actions' : 'Thao tác'}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map((cat) => (
                            <tr key={cat.id} className="border-b hover:bg-gray-50">
                                <td className="px-4 py-3">
                                    <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <CategoryIcon name={cat.icon} className="w-5 h-5 text-gray-700" />
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="font-medium">{language === 'en' ? cat.name_en : cat.name_vi}</div>
                                    <div className="text-sm text-gray-500">{language === 'en' ? cat.name_vi : cat.name_en}</div>
                                </td>
                                <td className="px-4 py-3">
                                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">{cat.slug}</code>
                                </td>
                                <td className="px-4 py-3">{cat.sort_order}</td>
                                <td className="px-4 py-3">
                                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${cat.is_active
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-gray-100 text-gray-600'
                                        }`}>
                                        {cat.is_active
                                            ? (language === 'en' ? 'Active' : 'Hoạt động')
                                            : (language === 'en' ? 'Hidden' : 'Ẩn')
                                        }
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        onClick={() => handleEdit(cat)}
                                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(cat.id)}
                                        className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {categories.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        {language === 'en' ? 'No categories found' : 'Chưa có danh mục nào'}
                    </div>
                )}
            </div>
        </div>
    )
}
